import {
	type Platform,
	parseSpec,
	SHORTCUTS,
	type ShortcutId,
	type Step
} from './shortcuts'

/**
 * Who answers each shortcut right now. A stack per id, and the last to mount
 * wins: two screens never answer the same key at once, but the one mounting
 * can overlap the one leaving by a render.
 */
export function createRegistry() {
	const handlers = new Map<ShortcutId, ((key: KeyLike) => void)[]>()

	return {
		handler: (id: ShortcutId) => handlers.get(id)?.at(-1),
		register(id: ShortcutId, handler: (key: KeyLike) => void) {
			handlers.set(id, [...(handlers.get(id) ?? []), handler])

			return () => {
				handlers.set(
					id,
					(handlers.get(id) ?? []).filter(
						(entry) => entry !== handler
					)
				)
			}
		}
	}
}

export type Registry = ReturnType<typeof createRegistry>

export type KeyLike = Pick<
	KeyboardEvent,
	'altKey' | 'ctrlKey' | 'key' | 'metaKey' | 'shiftKey'
>

export interface KeyContext {
	editable: boolean
	inDialog: boolean
	// Optional so a context that never selects text need not say so.
	textSelected?: boolean
	unfocused: boolean
}

/**
 * Turns one key press into «run this», «wait for the next key» or «not ours».
 *
 * Pure but for the half-typed sequence it remembers, which lives in this
 * closure and nowhere else. `now` comes in rather than being read, so a test
 * can say how long somebody paused between two keys.
 */
export function createDispatcher(
	platform: Platform,
	isActive: (id: ShortcutId) => boolean
) {
	const bindings = Object.entries(SHORTCUTS).flatMap(([id, shortcut]) =>
		shortcut[platform].map((spec) => ({
			id: id as ShortcutId,
			inFields: 'inFields' in shortcut,
			onlyUnfocused: 'onlyUnfocused' in shortcut,
			steps: parseSpec(spec),
			unlessTextSelected: 'unlessTextSelected' in shortcut
		}))
	)
	let pending: {
		at: number
		candidates: typeof bindings
		depth: number
	} | null = null

	return (event: KeyLike, context: KeyContext, now: number) => {
		// Pressing `Control` on its own is how somebody gets to `Ctrl+C`; it
		// must neither run anything nor break the sequence it is part of.
		if (MODIFIER_KEYS.has(event.key)) {
			return NONE
		}

		const live =
			pending && now - pending.at <= SEQUENCE_TIMEOUT_MS ? pending : null
		pending = null

		if (context.inDialog) {
			return NONE
		}

		const depth = live?.depth ?? 0
		const candidates =
			live?.candidates ??
			bindings.filter(
				(binding) => isActive(binding.id) && allowed(binding, context)
			)
		const matched = candidates.filter((binding) => {
			const step = binding.steps[depth]
			return step !== undefined && matches(step, event)
		})
		const done = matched.find(
			(binding) => binding.steps.length === depth + 1
		)

		if (done) {
			return { id: done.id, type: 'run' } as const
		}

		if (matched.length > 0) {
			pending = { at: now, candidates: matched, depth: depth + 1 }
			return WAIT
		}

		return NONE
	}
}

export type Dispatch = ReturnType<ReturnType<typeof createDispatcher>>

const SEQUENCE_TIMEOUT_MS = 1000
const MODIFIER_KEYS = new Set(['Alt', 'Control', 'Meta', 'Shift'])
const NONE = { type: 'none' } as const
// Letters, digits and space are the same key with or without Shift; any
// other single character is a symbol whose Shift the layout decides.
const TYPED_WITHOUT_SHIFT = /[a-z0-9 ]/
const WAIT = { type: 'wait' } as const

function allowed(
	binding: {
		inFields: boolean
		onlyUnfocused: boolean
		steps: Step[]
		unlessTextSelected: boolean
	},
	context: KeyContext
) {
	if (binding.onlyUnfocused && !context.unfocused) {
		return false
	}

	if (binding.unlessTextSelected && context.textSelected) {
		return false
	}

	if (context.editable) {
		const first = binding.steps[0]
		return (
			binding.inFields &&
			first !== undefined &&
			(first.meta || first.ctrl || first.alt)
		)
	}

	return true
}

function matches(step: Step, event: KeyLike) {
	if (event.key.toLowerCase() !== step.key) {
		return false
	}

	// A symbol's Shift belongs to the layout, not to the shortcut: `?` is
	// Shift+' on an Italian keyboard and Shift+/ on an American one.
	const shiftFree =
		step.key.length === 1 && !TYPED_WITHOUT_SHIFT.test(step.key)

	return (
		event.metaKey === step.meta &&
		event.ctrlKey === step.ctrl &&
		event.altKey === step.alt &&
		(shiftFree || event.shiftKey === step.shift)
	)
}
